import { Badge, Button } from '@mui/material';
import { mergeAthleteWorkoutLayers } from '@strength/domain-workout';
import {
  MdCropSquare,
  MdFilterList,
  MdGridView,
  MdOutlineArrowBack,
} from '@teamworksdev/icons';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SelectableOption } from '../../../../components/input/multi-select';
import { Tabs, TabTrigger } from '../../../../components/tab/tab';
import { toast } from '../../../../hooks/useToast';
import { ActualsSetFilter } from '../../set-select';
import { getOptionsFromWorkouts } from '../../utils';
import { useTabletContext } from '../context/TabletContext';
import { TabletExerciseFilters, WorkoutData } from '../types';
import { EndSessionDrawer } from './drawers/EndSessionDrawer';
import { FilterDrawer } from './drawers/FilterDrawer';

type ActionBarProps = {
  onExit: () => void;
  athleteWorkoutLayers: WorkoutData[];
  selectedAthletesWorkoutLayers: WorkoutData[] | undefined;
  filters: TabletExerciseFilters;
  onFiltersChange: (
    updater: (prev: TabletExerciseFilters) => TabletExerciseFilters,
  ) => void;
  unrecordedActuals: string[];
};

export const ActionBar = ({
  onExit,
  athleteWorkoutLayers,
  selectedAthletesWorkoutLayers,
  filters,
  onFiltersChange,
  unrecordedActuals,
}: ActionBarProps) => {
  const { t } = useTranslation();
  const {
    athleteSession,
    selectedAthletes,
    canShowSplitView,
    splitViewEnabled,
    onSetSplitView,
  } = useTabletContext();
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [endSessionDrawerOpen, setEndSessionDrawerOpen] = useState(false);
  const workouts = selectedAthletesWorkoutLayers
    ?.map((layers) => {
      return mergeAthleteWorkoutLayers(
        layers.blockLayer,
        layers,
        layers.modifier,
        layers.actual,
      );
    })
    .filter(
      (workout): workout is NonNullable<typeof workout> =>
        workout !== undefined,
    )
    .map((workout) => ({ workout }));

  const exerciseOptions = useMemo(
    () => getOptionsFromWorkouts(workouts ?? [], t),
    [workouts, t],
  );

  const doesAtLeastOneAthleteHaveWorkout = useMemo(
    () =>
      selectedAthletes.length > 0 &&
      selectedAthletes.some((athlete) => athleteSession[athlete.id]?.workoutId),
    [selectedAthletes, athleteSession],
  );

  const handleDisabledButtonClick = () => {
    toast({
      title: t('atLeastOneAthleteMustHaveWorkout'),
      severity: 'info',
    });
  };

  const handleFilterDrawerOpen = () => {
    setFilterDrawerOpen(true);
  };

  const handleFilterDrawerClose = () => {
    onFiltersChange((prev) => ({ ...prev }));
    setFilterDrawerOpen(false);
  };

  const handleExerciseSelection = (exerciseOptions: SelectableOption[]) => {
    onFiltersChange((prev) => ({ ...prev, exerciseFilters: exerciseOptions }));
  };

  const handleSetFilter = (newSetFilter: ActualsSetFilter) => {
    onFiltersChange((prev) => ({ ...prev, setFilter: newSetFilter }));
  };

  const handleEndSessionDrawerOpen = () => {
    setEndSessionDrawerOpen(true);
  };

  const handleEndSessionDrawerClose = () => {
    setEndSessionDrawerOpen(false);
  };

  const hasActiveFilters = useMemo(() => {
    return (
      filters.setFilter !== 'all-sets' || filters.exerciseFilters.length > 0
    );
  }, [filters]);

  return (
    <>
      <div className="flex flex-row justify-between items-center mr-4 mb-2">
        <Button
          variant="text"
          onClick={onExit}
          className="flex flex-row items-center gap-2"
        >
          <MdOutlineArrowBack size={20} />
          {t('exit')}
        </Button>
        <div className="flex flex-row items-center gap-2">
          <Tabs
            component="div"
            size="small"
            value={splitViewEnabled ? 'split' : 'single'}
            onChange={(_, newValue) => onSetSplitView(newValue === 'split')}
            className="w-[88px]"
          >
            <TabTrigger value="single" label={<MdCropSquare size={16} />} />
            <TabTrigger
              value="split"
              label={<MdGridView size={16} />}
              disabled={!canShowSplitView}
            />
          </Tabs>

          <div
            onClick={
              !doesAtLeastOneAthleteHaveWorkout
                ? handleDisabledButtonClick
                : undefined
            }
          >
            <Badge color="error" variant="dot" invisible={!hasActiveFilters}>
              <Button
                variant="outlined"
                className="p-2"
                disabled={!doesAtLeastOneAthleteHaveWorkout}
                onClick={handleFilterDrawerOpen}
              >
                <MdFilterList size={20} />
              </Button>
            </Badge>
          </div>
          <div
            onClick={
              !doesAtLeastOneAthleteHaveWorkout
                ? handleDisabledButtonClick
                : undefined
            }
          >
            <Button
              variant="outlined"
              className="p-2"
              disabled={!doesAtLeastOneAthleteHaveWorkout}
              onClick={handleEndSessionDrawerOpen}
            >
              {t('endSession')}
            </Button>
          </div>
        </div>
      </div>
      <FilterDrawer
        open={filterDrawerOpen}
        onClose={handleFilterDrawerClose}
        exerciseOptions={exerciseOptions?.exerciseOptions ?? []}
        exerciseOptionFilters={filters.exerciseFilters}
        setExerciseOptionFilters={handleExerciseSelection}
        setFilter={filters.setFilter}
        setSetFilter={handleSetFilter}
      />
      <EndSessionDrawer
        athleteWorkoutLayers={athleteWorkoutLayers}
        open={endSessionDrawerOpen}
        onClose={handleEndSessionDrawerClose}
        onExit={onExit}
        unrecordedActuals={unrecordedActuals}
      />
    </>
  );
};
